'use client';

import { useCallback } from 'react';
import { TransactionSignature, PublicKey } from '@solana/web3.js';
import { BN } from '@drift-labs/sdk';
import { VaultClient, WithdrawUnit } from '@drift-labs/vaults-sdk';
import { notify } from 'src/utils/notifications';
import { useGetTxParams } from '../transactions/useGetTxParams';
import { useWaitForTxConfirm } from '../transactions/useWaitForTxConfirm';
import { mapTxParamsToVaultTxParams } from './utils';

export const useRequestVaultWithdrawal = () => {
	const getTxParams = useGetTxParams();
	const { waitForTxConfirm } = useWaitForTxConfirm();

	const requestVaultWithdrawal = useCallback(
		async (
			vaultDepositorPubKey: PublicKey,
			userSharesPercentage: BN,
			vaultClient: VaultClient
		): Promise<TransactionSignature | undefined> => {
			if (!vaultClient) {
				console.error('No vault client provided');
				return undefined;
			}

			const toastId = 'requestVaultWithdrawal';

			try {
				notify({
					type: 'awaiting',
					message: 'Awaiting transaction confirmation',
					id: toastId,
					showUntilCancelled: true,
				});

				const txSig = await vaultClient.requestWithdraw(
					vaultDepositorPubKey,
					userSharesPercentage,
					WithdrawUnit.SHARES_PERCENT,
					mapTxParamsToVaultTxParams(getTxParams())
				);

				await waitForTxConfirm(txSig);

				notify({
					type: 'success',
					message: 'Withdrawal request successful.',
					id: toastId,
					updatePrevious: true,
					showUntilCancelled: false,
				});

				return txSig;
			} catch (err: any) {
				console.error(err);
				notify({
					type: 'error',
					message:
						'There was an error requesting a withdrawal from this vault.',
					id: toastId,
					updatePrevious: true,
					showUntilCancelled: false,
				});
				return undefined;
			}
		},
		[getTxParams, waitForTxConfirm]
	);

	return { requestVaultWithdrawal };
};
